// Dependencies
import React, { useState } from "react";
import { ActionMeta } from "react-select";

import { faSearch } from "@fortawesome/pro-regular-svg-icons";

// API
import { fetchEmployees } from "../../api/services";

// Utils
import Cookies from "../../utils/Cookies";

// Components
import EmployeePicker, {
	EmployeeGroup,
	EmployeeOption,
	BaseProps as EmployeePickerProps,
	SingleEmployee,
} from "../EmployeePicker";

export interface BaseProps extends Pick<
	EmployeePickerProps,
	"value" | "fetchOptions" | "onChange" | "placeholder" | "queryKey"
> {
	selectSearchHistory?: () => SingleEmployee[];
	setEmployeeHistory?: (nextSearchHistory: SingleEmployee[], cookieName: string) => void;
	cookieName?: string;
}

/**
 * Gets the employee search history from a cookie.
 */
export const getEmployeeHistory = (cookieName: string) => {
	try {
		const cookie = Cookies.get(cookieName, "[]");
		if (cookie) {
			const searchHistory: SingleEmployee[] = JSON.parse(cookie).map((employee: SingleEmployee) => {
				return {
					label: employee?.label,
					value: employee?.value,
				};
			});

			return searchHistory;
		} else {
			return [];
		}
	} catch (e) {
		return [];
	}
};

/**
 * Sets the employee search history in a cookie.
 */
export const setEmployeeHistory = (nextSearchHistory: SingleEmployee[], cookieName: string) => {
	// remove duplicates by id
	const employees = nextSearchHistory.filter(
		(employee, index, self) => index === self.findIndex((t) => t.value === employee.value)
	);

	Cookies.set(cookieName, JSON.stringify(employees));
};

export const removeSearchHistoryItem = (searchHistory, ii, cookieName: string) => {
	const recentSearchEmployees = [...searchHistory];
	recentSearchEmployees.splice(ii, 1);
	Cookies.set(cookieName, JSON.stringify(recentSearchEmployees));
	return recentSearchEmployees;
};

/**
 * The Employee Search component makes use of the EmployeePicker's flexibility
 * by wrapping it and extending it's functionality. It adds a recent searches
 * section to the dropdown menu and also allows for the search history to be
 * stored in a cookie.
 *
 * This component is also flexible and provides props to override the default
 * search history and fetch options methods.
 */
const EmployeeSearch = (props: BaseProps) => {
	const {
		value,
		placeholder = "Search employee...",
		queryKey = "employeePickerSearch",
		cookieName = "employeeSearchHistory",
	} = props;

	const [searchHistory, setSearchHistory] = useState(
		props.selectSearchHistory ? props.selectSearchHistory() : getEmployeeHistory(cookieName)
	);

	/**
	 * The handleChange method is called when the user selects an employee
	 * from the dropdown menu. It will add the selected employee to the
	 * search history and then call the onChange method that was passed
	 * in as a prop.
	 */
	const handleChange = (value: SingleEmployee | null, actionMeta: ActionMeta<EmployeeOption>) => {
		if (actionMeta.action === "clear") {
			props.onChange(null, actionMeta);
		} else if (value) {
			const newSearchHistory = [...getEmployeeHistory(cookieName), { value: value.value, label: value.label }];

			if (props.setEmployeeHistory) {
				props.setEmployeeHistory(newSearchHistory, cookieName);
			} else {
				setEmployeeHistory(newSearchHistory, cookieName);
			}

			props.onChange(value, actionMeta);
		}
	};

	/**
	 * Fetches employee data from the API and returns it in the format
	 * that the EmployeePicker expects. If the search input is empty
	 * and there is search history, it will return the search history.
	 */
	const fetchOptions = async (inputValue: string) => {
		// we only show the search history if it exists and there is no
		// search input. As soon as the user types something, the search
		// history is removed.
		if (inputValue === "") {
			if (searchHistory.length === 0) {
				return [];
			} else if (searchHistory.length > 0) {
				return [
					{
						label: "Recent Searches",
						// add showRemove key to make options removable
						options: searchHistory.map((employee) => ({ ...employee, showRemove: true })),
					},
				] satisfies EmployeeGroup[];
			}
		}

		// allow the default fetch employees behavior to be overridden
		if (props.fetchOptions) {
			return props.fetchOptions(inputValue);
		}

		const data = await fetchEmployees({
			searchTerm: inputValue,
		});

		return data.users.map((user) => {
			return {
				label: user.name,
				value: user._id,
			};
		});
	};

	const handleRemoveOption = (removedEmployee) => {
		const newSearchHistory = getEmployeeHistory(cookieName).filter(
			(employee) => employee.value !== removedEmployee
		);

		if (props.setEmployeeHistory) {
			props.setEmployeeHistory(newSearchHistory, cookieName);
		} else {
			setEmployeeHistory(newSearchHistory, cookieName);
		}

		setSearchHistory(newSearchHistory);
	};

	return (
		<EmployeePicker
			// add key to force re-render when search history changes
			key={searchHistory.length}
			inputId="search-input"
			value={value}
			onChange={handleChange as () => void}
			leftIcon={faSearch}
			isClearable
			fetchOptions={fetchOptions}
			placeholder={placeholder}
			openMenuOnFocus={true}
			openMenuOnClick={false}
			onRemoveOption={handleRemoveOption}
			maxHeight={160}
			queryKey={queryKey}
			dropdownIndicator={false}
		/>
	);
};

export default EmployeeSearch;
